import {
  createContext,
  useContext,
  useMemo,
} from "react";
import {
  useNavigate,
  Routes,
  Route,
  Navigate,
  Link,
  NavLink,
  useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { useOwnerProfileForm } from "./ownerProfileForm";
import AdminOwnerProfileSettingsBody from "./AdminOwnerProfileSettingsBody";
import AdminOwnerSocialSettings from "./AdminOwnerSocialSettings";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { normalizeRole } from "../../../utils/roles";
import { ROLE_LABEL } from "./staffProfileShared";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { Avatar } from "../../ui";
import cs from "../PatientPortal/clientApp.module.css";
import aa from "./adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import L from "../../../constants/baysportLabels";

const DEFAULT_PROFILE_BASE = "/manage/app/profile";
const DEFAULT_ACCOUNT_BASE = `${DEFAULT_PROFILE_BASE}/account`;

const ProfileAccountCtx = createContext({
  profileBase: DEFAULT_PROFILE_BASE,
  accountBase: DEFAULT_ACCOUNT_BASE,
  showBilling: true,
});

function useProfileAccountPaths() {
  return useContext(ProfileAccountCtx);
}

const ALL_MENU = [
  { id: "data", label: "Личные данные", hint: "ФИО, телефон, фото" },
  { id: "social", label: "Оформление", hint: "Био и цвет профиля" },
  {
    id: "billing",
    label: "Подписка CRM",
    hint: `Тариф и оплата ${L.productName}`,
  },
  { id: "pwd", label: "Пароль", hint: "Безопасность входа" },
];

function OwnerProfShell({ children }) {
  return <div className={`${aa.ownerProfChrome} ${cs.profPage}`}>{children}</div>;
}

function AccountMenu() {
  const navigate = useNavigate();
  const { profileBase, accountBase, showBilling } = useProfileAccountPaths();
  const { profile, tenant, role } = useSelector((state) => state.auth);
  const roleLabel = ROLE_LABEL[normalizeRole(role)] || role;
  const menu = ALL_MENU.filter((item) => showBilling || item.id !== "billing");

  return (
    <OwnerProfShell>
      <header className={cs.profSubHead}>
        <button
          type="button"
          className={cs.profBackBtn}
          onClick={() => navigate(profileBase)}
        >
          ← Профиль
        </button>
        <h1>Настройки</h1>
        <p>
          {roleLabel} · {tenant?.name}
        </p>
      </header>

      <div className={cs.profAccountUser}>
        <button
          type="button"
          className={cs.profAccountUserMain}
          onClick={() => navigate(`${accountBase}/data`)}
        >
          <div className={cs.profAccountUserAvatar}>
            <Avatar
              name={profile?.full_name || "?"}
              src={mediaUrl(profile?.photo_url)}
              size="md"
            />
          </div>
          <div className={cs.profAccountUserText}>
            <strong>{profile?.full_name}</strong>
            <span>{tenant?.name}</span>
            <span className={cs.profAccountUserEdit}>Личные данные и фото</span>
          </div>
          <span className={cs.profAccountUserChevron} aria-hidden>
            ›
          </span>
        </button>
      </div>

      <nav className={cs.profNavList} aria-label="Настройки аккаунта">
        {menu.map((item) => (
          <button
            key={item.id}
            type="button"
            className={cs.profNavRow}
            onClick={() => navigate(`${accountBase}/${item.id}`)}
          >
            <span className={cs.profNavText}>
              <strong>{item.label}</strong>
              <small>{item.hint}</small>
            </span>
            <span className={cs.profNavChevron} aria-hidden>
              ›
            </span>
          </button>
        ))}
      </nav>
    </OwnerProfShell>
  );
}

function AccountSubScreen({ screenId }) {
  const navigate = useNavigate();
  const { accountBase, showBilling } = useProfileAccountPaths();
  const profileForm = useOwnerProfileForm();
  const item = ALL_MENU.find((m) => m.id === screenId);

  if (screenId === "billing" && !showBilling) {
    return <Navigate to={accountBase} replace />;
  }

  const bodyClass =
    screenId === "social" ? cs.profSocialWrap : cs.profSubBody;

  return (
    <OwnerProfShell>
      <header className={cs.profSubHead}>
        <button
          type="button"
          className={cs.profBackBtn}
          onClick={() => navigate(accountBase)}
        >
          ← Настройки
        </button>
        <h1>{item?.label}</h1>
        {item?.hint ? <p>{item.hint}</p> : null}
      </header>
      <div className={bodyClass}>
        {screenId === "data" ? (
          <AdminOwnerProfileSettingsBody
            {...profileForm}
            showBilling={false}
            showPassword={false}
          />
        ) : null}
        {screenId === "billing" ? (
          <AdminOwnerProfileSettingsBody
            {...profileForm}
            showData={false}
            showPassword={false}
          />
        ) : null}
        {screenId === "pwd" ? (
          <AdminOwnerProfileSettingsBody
            {...profileForm}
            showData={false}
            showBilling={false}
          />
        ) : null}
        {screenId === "social" ? <AdminOwnerSocialSettings /> : null}
      </div>
    </OwnerProfShell>
  );
}

function AccountDesktopLayout() {
  const { section } = useParams();
  const { profileBase, accountBase, showBilling } = useProfileAccountPaths();
  const profileForm = useOwnerProfileForm();
  const { profile, tenant, role } = useSelector((state) => state.auth);
  const roleLabel = ROLE_LABEL[normalizeRole(role)] || role;
  const menu = ALL_MENU.filter((item) => showBilling || item.id !== "billing");
  const active = menu.find((m) => m.id === section);

  if (!active) {
    return <Navigate to={`${accountBase}/data`} replace />;
  }

  const renderContent = () => {
    switch (section) {
      case "data":
        return (
          <AdminOwnerProfileSettingsBody
            {...profileForm}
            showBilling={false}
            showPassword={false}
          />
        );
      case "billing":
        return (
          <AdminOwnerProfileSettingsBody
            {...profileForm}
            showData={false}
            showPassword={false}
          />
        );
      case "pwd":
        return (
          <AdminOwnerProfileSettingsBody
            {...profileForm}
            showData={false}
            showBilling={false}
          />
        );
      case "social":
        return <AdminOwnerSocialSettings />;
      default:
        return null;
    }
  };

  return (
    <div className={`${md.desktopHubPage} ${md.desktopProfileAccountPage}`}>
      <Link to={profileBase} className={md.desktopHubBack}>
        ← Профиль
      </Link>

      <div className={md.desktopProfileAccountLayout}>
        <aside className={md.desktopProfileAccountAside}>
          <div className={md.desktopProfileAccountUser}>
            <Avatar
              name={profile?.full_name || "?"}
              src={mediaUrl(profile?.photo_url)}
              size="md"
            />
            <div className={md.desktopProfileAccountUserText}>
              <strong>{profile?.full_name || "—"}</strong>
              <span>
                {roleLabel} · {tenant?.name || "—"}
              </span>
            </div>
          </div>

          <nav className={md.desktopProfileAccountNav} aria-label="Настройки аккаунта">
            {menu.map((item) => (
              <NavLink
                key={item.id}
                to={`${accountBase}/${item.id}`}
                className={({ isActive }) =>
                  isActive
                    ? md.desktopProfileAccountNavOn
                    : md.desktopProfileAccountNavItem
                }
              >
                <span className={md.desktopProfileAccountNavText}>
                  <strong>{item.label}</strong>
                  <span>{item.hint}</span>
                </span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <section className={md.desktopProfileAccountPanel}>
          <header className={md.desktopProfileAccountPanelHead}>
            <h2 className={md.desktopProfileAccountPanelTitle}>{active.label}</h2>
            <p className={md.desktopProfileAccountPanelHint}>{active.hint}</p>
          </header>
          <div
            className={[
              md.desktopProfileSettings,
              section === "social" ? md.desktopProfileSocialSettings : "",
              section === "billing" ? md.desktopProfileBillingSettings : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {renderContent()}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function AdminOwnerProfileAccount({
  profileBase = DEFAULT_PROFILE_BASE,
  accountBase = DEFAULT_ACCOUNT_BASE,
  showBilling = true,
}) {
  const isApp = useAdminHubUi();
  const isMobile = useIsMobile(768);
  const isDesktopManage = !isApp && !isMobile;
  const paths = useMemo(
    () => ({ profileBase, accountBase, showBilling }),
    [profileBase, accountBase, showBilling],
  );

  return (
    <ProfileAccountCtx.Provider value={paths}>
      {isDesktopManage ? (
        <Routes>
          <Route index element={<Navigate to="data" replace />} />
          <Route path=":section" element={<AccountDesktopLayout />} />
        </Routes>
      ) : (
        <Routes>
          <Route index element={<AccountMenu />} />
          <Route path="data" element={<AccountSubScreen screenId="data" />} />
          {showBilling ? (
            <Route
              path="billing"
              element={<AccountSubScreen screenId="billing" />}
            />
          ) : null}
          <Route
            path="social"
            element={<AccountSubScreen screenId="social" />}
          />
          <Route path="pwd" element={<AccountSubScreen screenId="pwd" />} />
          <Route path="*" element={<Navigate to={accountBase} replace />} />
        </Routes>
      )}
    </ProfileAccountCtx.Provider>
  );
}
